import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { changeAccountRole, reactivateAccount, resetAccountPassword, suspendAccount } from '../api/endpoints';
import type { AccountProfile, AdminAccount } from '../api/types';
import { generatePassword } from './generatePassword';
import { PasswordField } from './PasswordField';

const ASSIGNABLE_ROLES: AccountProfile['role'][] = ['PARENT', 'SUPPORT', 'CONTENT_EDITOR', 'SUPERUSER'];

/**
 * The privileged actions on one account.
 *
 * Two things are deliberate. Suspension asks for a reason and will not proceed without one — a privileged
 * action with no stated reason is unreviewable six months later, and the audit trail is only as useful as
 * what was written into it. And the API refuses an account acting on itself, so the menu hides those items
 * rather than offering a button that answers 422: the API is the control, this is only the manners.
 */
export const AccountActions = ({ account, actorId }: { account: AdminAccount; actorId: string | null }) => {
  const queryClient = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [suspending, setSuspending] = useState(false);
  const [reason, setReason] = useState('');
  const [roleDialog, setRoleDialog] = useState(false);
  const [role, setRole] = useState<string>(account.role);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [password, setPassword] = useState('');

  const isSelf = actorId === account.id;

  // Both keys, deliberately: every staff action writes an audit entry, so the audit list is stale unless
  // it is invalidated too. `useAdminMutation` does this for newer screens; these three mutations predate
  // it and are left as they are rather than rewritten in a bug fix (R0070).
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['audit'] });
  };

  const suspend = useMutation({
    mutationFn: () => suspendAccount(account.id, reason),
    onSuccess: () => {
      setSuspending(false);
      setReason('');
      refresh();
    },
  });

  const reactivate = useMutation({
    mutationFn: () => reactivateAccount(account.id),
    onSuccess: refresh,
  });

  const resetPassword = useMutation({
    mutationFn: () => resetAccountPassword(account.id, password),
    onSuccess: () => {
      setPasswordDialog(false);
      // Cleared on the way out. The dialog is the only place this value was ever meant to exist, and
      // leaving it in state means the next account's dialog opens holding the previous one's password.
      setPassword('');
      refresh();
    },
  });

  const changeRole = useMutation({
    mutationFn: () => changeAccountRole(account.id, role),
    onSuccess: () => {
      setRoleDialog(false);
      refresh();
    },
  });

  return (
    <>
      <IconButton size="small" onClick={(event) => setAnchor(event.currentTarget)} aria-label="Akcie účtu">
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {account.status === 'ACTIVE' && !isSelf && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setSuspending(true);
            }}
          >
            Pozastaviť účet
          </MenuItem>
        )}
        {account.status !== 'ACTIVE' && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              reactivate.mutate();
            }}
          >
            Obnoviť účet
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setRole(account.role);
            setRoleDialog(true);
          }}
        >
          Zmeniť rolu
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setPassword(generatePassword());
            setPasswordDialog(true);
          }}
        >
          Nastaviť nové heslo
        </MenuItem>
      </Menu>

      <Dialog open={suspending} onClose={() => setSuspending(false)} fullWidth maxWidth="sm">
        <DialogTitle>Pozastaviť {account.email}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Prihlásenie sa okamžite odmietne — účtu aj všetkým detským profilom pod ním. Dá sa to vrátiť.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Dôvod"
            helperText="Zapíše sa do auditu. Bez dôvodu sa akcia nedá vykonať."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspending(false)}>Zrušiť</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={reason.trim().length < 3 || suspend.isPending}
            onClick={() => suspend.mutate()}
          >
            Pozastaviť
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={passwordDialog}
        onClose={() => {
          setPasswordDialog(false);
          setPassword('');
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nové heslo pre {account.email}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Doterajšie heslo prestane platiť okamžite. Účet sa neodhlási — už vydané tokeny dobehnú.
          </DialogContentText>
          <PasswordField value={password} onChange={setPassword} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPasswordDialog(false);
              setPassword('');
            }}
          >
            Zrušiť
          </Button>
          <Button
            variant="contained"
            disabled={password.trim().length === 0 || resetPassword.isPending}
            onClick={() => resetPassword.mutate()}
          >
            Nastaviť
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={roleDialog} onClose={() => setRoleDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Rola pre {account.email}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Back-office rola sprístupní údaje o všetkých deťoch na platforme.
          </DialogContentText>
          <TextField
            select
            fullWidth
            label="Rola"
            value={role}
            onChange={(event) => setRole(event.target.value)}
          >
            {ASSIGNABLE_ROLES.map((option) => (
              <MenuItem key={option} value={option} disabled={isSelf && option !== 'SUPERUSER'}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog(false)}>Zrušiť</Button>
          <Button
            variant="contained"
            disabled={role === account.role || changeRole.isPending}
            onClick={() => changeRole.mutate()}
          >
            Uložiť
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
